"use client";
import React, { useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface CarouselNavProps {
  title?: string;
  onPrevious?: () => void;
  onNext?: () => void;
  currentIndex?: number;
  totalItems?: number;
  className?: string;
}

export function CarouselNav({
  title = "View Our Services",
  onPrevious,
  onNext,
  currentIndex = 0,
  totalItems = 1,
  className = ""
}: CarouselNavProps) {
  return (
    <div className={`relative w-full max-w-[1200px] mx-auto ${className}`}>
      {/* Main Container */}
      <div className="relative h-[200px] flex flex-col items-center justify-start pt-[89px]">
        
        {/* Centered Title Link */}
        <div className="mb-8">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="text-center px-4 py-2 rounded-lg transition-colors duration-200 hover:bg-gray-50"
            style={{ 
              color: 'rgb(30, 29, 28)',
              fontSize: '16px',
              fontWeight: '500',
              letterSpacing: '0.02em'
            }}
          >
            {title}
          </motion.button>
        </div>

        {/* Horizontal Divider Line */}
        <div 
          className="absolute left-0 right-0"
          style={{ 
            top: '0px',
            height: '1px',
            backgroundColor: 'rgb(82, 76, 61)',
            width: '100vw',
            marginLeft: 'calc(-50vw + 50%)'
          }}
        />

        {/* Navigation Arrows */}
        {totalItems > 1 && (
          <>
            {/* Left Arrow */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onPrevious}
              disabled={currentIndex === 0}
              className="absolute left-[30px] top-[168px] w-[30px] h-[30px] rounded-full border flex items-center justify-center transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ 
                backgroundColor: 'white',
                borderColor: 'rgb(82, 76, 61)',
                color: 'rgb(82, 76, 61)'
              }}
            >
              <ChevronLeft size={20} />
            </motion.button>

            {/* Right Arrow */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onNext}
              disabled={currentIndex >= totalItems - 1}
              className="absolute right-[30px] top-[168px] w-[30px] h-[30px] rounded-full border flex items-center justify-center transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ 
                backgroundColor: 'white',
                borderColor: 'rgb(82, 76, 61)',
                color: 'rgb(82, 76, 61)'
              }}
            >
              <ChevronRight size={20} />
            </motion.button>
          </>
        )}

        {/* Progress Indicator */}
        {totalItems > 1 && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
            {Array.from({ length: totalItems }).map((_, index) => (
              <div
                key={index}
                className="w-2 h-2 rounded-full transition-all duration-200"
                style={{
                  backgroundColor: index === currentIndex 
                    ? 'rgb(82, 76, 61)' 
                    : 'rgb(200, 200, 200)'
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface HudsonCarouselProps {
  items: React.ReactNode[];
  title?: string;
  autoPlay?: boolean;
  autoPlayDelay?: number;
}

export function HudsonCarousel({ 
  items, 
  title = "Explore Our Services",
  autoPlay = false,
  autoPlayDelay = 4000
}: HudsonCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  React.useEffect(() => {
    if (autoPlay && items.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % items.length);
      }, autoPlayDelay);

      return () => clearInterval(interval);
    }
  }, [autoPlay, autoPlayDelay, items.length]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % items.length);
  };

  return (
    <div className="w-full">
      {/* Carousel Navigation */}
      <CarouselNav
        title={title}
        onPrevious={handlePrevious}
        onNext={handleNext}
        currentIndex={currentIndex}
        totalItems={items.length}
      />

      {/* Carousel Content */}
      <div className="relative overflow-hidden">
        <motion.div
          className="flex"
          animate={{ x: `${-currentIndex * 100}%` }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 30,
          }}
        >
          {items.map((item, index) => (
            <div key={index} className="w-full flex-shrink-0">
              {item}
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
} 